import { cn } from "@/shared/lib/utils";

/**
 * memoru brand artwork: the "Note and moment" mark and the `memoru.` wordmark.
 *
 * A note, and the moment that mattered resting on its corner. Geometry and
 * colours are the brand sheet's. The mark contrasts with its surface: the black
 * tile on light backgrounds, the white tile on dark ones (it follows the theme).
 * Rules: minimum 16px, no note lines at 24px and below, the dot is the only
 * brand colour, and no gradients, shadows, outlines or rotation.
 */

/** Above this size the two note lines are drawn; at or below it they drop out. */
const NOTE_LINES_ABOVE = 24;

/** Decorative: pair it with the wordmark or an accessible name. */
export function BrandMark({ size = 24, className }: { size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      className={cn("shrink-0", className)}
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M32 1 C56 1 63 8 63 32 C63 56 56 63 32 63 C8 63 1 56 1 32 C1 8 8 1 32 1 Z"
        strokeWidth={1}
        className="fill-zinc-900 stroke-[rgba(255,255,255,0.14)] dark:fill-white dark:stroke-zinc-200"
      />
      <rect x={13} y={22} width={28} height={29} rx={6} className="fill-zinc-50 dark:fill-zinc-900" />
      {size > NOTE_LINES_ABOVE && (
        <>
          <rect x={18} y={37} width={18} height={4} rx={2} className="fill-zinc-900 dark:fill-white" />
          <rect x={18} y={44} width={11} height={4} rx={2} className="fill-zinc-900 dark:fill-white" />
        </>
      )}
      <circle
        cx={41}
        cy={23}
        r={11}
        strokeWidth={3.5}
        className="fill-[#76a9fa] stroke-zinc-900 dark:fill-[#1a73e8] dark:stroke-white"
      />
    </svg>
  );
}

/** `memoru.` in Geist 700, -0.04em, with the period in brand colour. */
export function Wordmark({ className }: { className?: string }) {
  return (
    <span className={cn("font-heading font-bold tracking-[-0.04em]", className)}>
      memoru<span className="text-[#1a73e8] dark:text-[#76a9fa]">.</span>
    </span>
  );
}

/** The mark beside the wordmark. */
export function BrandLockup({
  markSize = 24,
  className,
  wordmarkClassName = "text-lg",
}: {
  markSize?: number;
  className?: string;
  wordmarkClassName?: string;
}) {
  return (
    <span className={cn("inline-flex items-center gap-2", className)}>
      <BrandMark size={markSize} />
      <Wordmark className={wordmarkClassName} />
    </span>
  );
}
